/**
 * GitSyncDriver — drives `backend: 'git'` libraries.
 *
 * status/pull are permitted for owner | contributor | reader.
 * push is permitted only for owner.
 * propose is permitted only for contributor (fork + branch + PR via `gh` CLI).
 * publish is never permitted.
 *
 * @docLink packages/library/concepts#sync-driver
 */

import { type SpawnSyncReturns, spawnSync } from "node:child_process";
import {
  type AssetRef,
  type GitBackendConfig,
  type Library,
  LibrarySyncError,
  OperationNotPermittedError,
  type ProposeOpts,
  type ProposeResult,
  type PublishOpts,
  type PublishResult,
  type PullOpts,
  type PullResult,
  type PushOpts,
  type PushResult,
  type SyncStatus,
} from "../user-library.js";
import type { LibrarySyncDriver } from "./driver.js";

function git(cwd: string, args: string[]): SpawnSyncReturns<string> {
  return spawnSync("git", args, { cwd, encoding: "utf-8" });
}

function ok(r: SpawnSyncReturns<string>, action: string): string {
  if (r.status !== 0) {
    throw new LibrarySyncError(
      `git ${action} failed: ${r.stderr.trim() || r.stdout.trim()}`,
      "remote_error",
    );
  }
  return r.stdout.trim();
}

function gitCfg(lib: Library): GitBackendConfig {
  return lib.backendConfig as GitBackendConfig;
}

export class GitSyncDriver implements LibrarySyncDriver {
  readonly backend = "git" as const;

  async status(lib: Library): Promise<SyncStatus> {
    const cfg = gitCfg(lib);
    const porcelain = git(lib.path, ["status", "--porcelain"]);
    if (porcelain.status !== 0) {
      return {
        reachable: false,
        localChanges: { added: [], modified: [], deleted: [] },
        notes: [`git not initialised at ${lib.path}`],
      };
    }
    const added: string[] = [];
    const modified: string[] = [];
    const deleted: string[] = [];
    for (const line of porcelain.stdout.split("\n").filter(Boolean)) {
      const xy = line.slice(0, 2);
      const file = line.slice(3);
      if (xy.includes("A") || xy.startsWith("??")) added.push(file);
      else if (xy.includes("D")) deleted.push(file);
      else if (xy.includes("M") || xy.includes("R")) modified.push(file);
    }
    // Ahead/behind vs upstream.
    let remoteChanges: { behind: number; ahead: number } | undefined;
    const fetch = git(lib.path, ["fetch", "--quiet"]);
    const reachable = fetch.status === 0;
    if (reachable) {
      const counts = git(lib.path, [
        "rev-list",
        "--left-right",
        "--count",
        `${cfg.branch}...origin/${cfg.branch}`,
      ]);
      if (counts.status === 0) {
        const [ahead, behind] = counts.stdout.trim().split(/\s+/).map(Number);
        remoteChanges = { ahead: ahead || 0, behind: behind || 0 };
      }
    }
    return {
      reachable,
      localChanges: { added, modified, deleted },
      remoteChanges,
      notes: reachable ? [] : ["unable to reach origin"],
    };
  }

  async pull(lib: Library, opts?: PullOpts): Promise<PullResult> {
    const cfg = gitCfg(lib);
    const args = ["pull", "--ff-only", "origin", cfg.branch];
    if (opts?.rebase) args.splice(1, 1, "--rebase");
    const r = git(lib.path, args);
    if (r.status !== 0) {
      if (/non-fast-forward|diverged|conflict/i.test(r.stderr)) {
        return { applied: 0, conflicts: [{ path: "(branch)", reason: r.stderr.trim() }] };
      }
      throw new LibrarySyncError(`git pull failed: ${r.stderr.trim()}`, "remote_error");
    }
    return { applied: 1, conflicts: [] };
  }

  async push(lib: Library, opts?: PushOpts): Promise<PushResult> {
    if (lib.ownership !== "owner") {
      throw new OperationNotPermittedError(
        "push",
        `library "${lib.name}" has ownership=${lib.ownership}; use \`propose\` instead`,
      );
    }
    const cfg = gitCfg(lib);
    const args = ["push", "origin", cfg.branch];
    if (opts?.force) args.push("--force-with-lease");
    ok(git(lib.path, args), "push");
    return { pushed: 1 };
  }

  async propose(lib: Library, opts: ProposeOpts): Promise<ProposeResult> {
    if (lib.ownership !== "contributor") {
      throw new OperationNotPermittedError(
        "propose",
        `propose requires ownership=contributor; got ${lib.ownership}`,
      );
    }
    const cfg = gitCfg(lib);
    const branch =
      opts.branch ??
      `skaile/${new Date()
        .toISOString()
        .replace(/[-:.TZ]/g, "")
        .slice(0, 14)}`;
    // Ensure fork remote exists.
    const remotes = ok(git(lib.path, ["remote"]), "remote-list").split("\n");
    if (!remotes.includes("fork")) {
      if (!cfg.forkUrl) {
        // Create fork via gh.
        const fork = spawnSync(
          "gh",
          ["repo", "fork", cfg.url, "--remote", "--remote-name", "fork"],
          {
            cwd: lib.path,
            encoding: "utf-8",
          },
        );
        if (fork.status !== 0) {
          throw new LibrarySyncError(
            `gh repo fork failed: ${fork.stderr.trim()}. Manual fallback: fork ${cfg.url} on github.com and run \`skaile library set ${lib.name} backendConfig.forkUrl=<url>\`.`,
            "auth_required",
          );
        }
      } else {
        ok(git(lib.path, ["remote", "add", "fork", cfg.forkUrl]), "remote-add");
      }
    }
    // Create / push branch.
    ok(git(lib.path, ["checkout", "-B", branch]), "checkout");
    ok(git(lib.path, ["push", "fork", branch]), "push-fork");

    // Open PR via gh.
    const ghArgs = ["pr", "create", "--base", cfg.branch, "--head", `fork:${branch}`];
    if (opts.title) ghArgs.push("--title", opts.title);
    if (opts.body) ghArgs.push("--body", opts.body);
    const pr = spawnSync("gh", ghArgs, { cwd: lib.path, encoding: "utf-8" });
    if (pr.status !== 0) {
      const manualUrl = `${cfg.url.replace(/\.git$/, "")}/compare/${cfg.branch}...${branch}`;
      throw new LibrarySyncError(
        `gh pr create failed: ${pr.stderr.trim()}. Open manually: ${manualUrl}`,
        "remote_error",
      );
    }
    const prUrl = pr.stdout.trim().split("\n").pop() ?? "(unknown)";
    return { prUrl, branch };
  }

  async publish(_lib: Library, _refs: AssetRef[], _opts: PublishOpts): Promise<PublishResult> {
    throw new OperationNotPermittedError("publish", "git libraries cannot publish; use store");
  }
}
